import { useMemo, useRef, useState } from 'react';
import { i18n } from '@wms/shared';
import { matchesQuery } from '../lib/search.js';
import { formatQty } from '../lib/format.js';

const { t } = i18n;

export interface SearchOption {
  id: string;
  label: string;
  /** Optional thumbnail shown beside the option text. */
  imageUrl?: string | null;
  /** Live usable stock shown before the thumbnail and label (WMS-63). */
  availableQty?: number;
  /** Product units that can be assembled from currently usable components. */
  maxProducibleQty?: number;
  /** Extra text to match against (e.g. English name) beyond the label. */
  keywords?: string;
}

/**
 * A type-to-filter replacement for a long `<select>` (QA #7 / WMS-8). Shows the
 * selected option's label; on focus you can type to narrow the list. Matching
 * is diacritic-insensitive via the shared `matchesQuery`.
 */
export function SearchSelect({
  value,
  onChange,
  options,
  placeholder,
  ariaLabel,
  className,
  menuSize = 'default',
}: {
  value: string;
  onChange: (id: string) => void;
  options: readonly SearchOption[];
  placeholder?: string;
  ariaLabel?: string;
  className?: string;
  /** Taller result list for full-screen catalogue pickers (WMS-47/48). */
  menuSize?: 'default' | 'tall';
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const gesture = useRef<{ x: number; y: number; started: number; cancelled: boolean } | null>(
    null,
  );
  const selected = options.find((o) => o.id === value);
  const filtered = useMemo(
    () => options.filter((o) => matchesQuery(query, o.label, o.keywords)).slice(0, 50),
    [options, query],
  );

  return (
    <div
      className={`relative ${className ?? ''}`}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}
    >
      <input
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-label={ariaLabel}
        value={open ? query : (selected?.label ?? '')}
        placeholder={placeholder ?? selected?.label ?? t('common.select')}
        onFocus={() => {
          setQuery('');
          setOpen(true);
        }}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        className="w-full rounded border border-border bg-background px-2 py-1"
      />
      {open ? (
        <ul
          className={`absolute z-50 mt-1 w-full overflow-auto rounded border border-border bg-background text-sm shadow-lg ${
            menuSize === 'tall' ? 'max-h-[65dvh]' : 'max-h-60'
          }`}
        >
          {filtered.length === 0 ? (
            <li className="px-2 py-1 text-xs text-muted-foreground">{t('search.no_results')}</li>
          ) : (
            filtered.map((o) => (
              <li key={o.id}>
                <button
                  type="button"
                  // Keep focus for a mouse click without blocking native touch
                  // scrolling. Commit only after release, never on initial touch.
                  onMouseDown={(event) => event.preventDefault()}
                  onPointerDown={(event) => {
                    gesture.current = {
                      x: event.clientX,
                      y: event.clientY,
                      started: Date.now(),
                      cancelled: false,
                    };
                  }}
                  onPointerMove={(event) => {
                    const start = gesture.current;
                    if (start && Math.hypot(event.clientX - start.x, event.clientY - start.y) > 8)
                      start.cancelled = true;
                  }}
                  onPointerCancel={() => {
                    if (gesture.current) gesture.current.cancelled = true;
                  }}
                  onClick={(event) => {
                    const start = gesture.current;
                    gesture.current = null;
                    // detail=0 is keyboard/assistive activation, not a swipe.
                    if (
                      event.detail !== 0 &&
                      start &&
                      (start.cancelled || Date.now() - start.started > 600)
                    )
                      return;
                    onChange(o.id);
                    setQuery('');
                    setOpen(false);
                  }}
                  className={`flex w-full items-center gap-2 px-2 py-1 text-left hover:bg-muted ${
                    o.id === value ? 'bg-muted' : ''
                  }`}
                >
                  {o.availableQty !== undefined ? (
                    <span
                      className="min-w-16 shrink-0 text-right font-semibold tabular-nums"
                      title={
                        o.maxProducibleQty === undefined
                          ? t('stock_dash.col.available')
                          : `${t('stock_dash.col.available')} / ${t('bom.max_producible')}`
                      }
                      aria-label={
                        o.maxProducibleQty === undefined
                          ? `${t('stock_dash.col.available')}: ${formatQty(o.availableQty)}`
                          : `${t('stock_dash.col.available')}: ${formatQty(o.availableQty)}; ${t('bom.max_producible')}: ${formatQty(o.maxProducibleQty)}`
                      }
                    >
                      {formatQty(o.availableQty)}
                      {o.maxProducibleQty === undefined
                        ? null
                        : `/${formatQty(o.maxProducibleQty)}`}
                    </span>
                  ) : null}
                  {o.imageUrl ? (
                    <img
                      src={o.imageUrl}
                      alt=""
                      className="h-9 w-9 shrink-0 rounded border border-border object-cover"
                    />
                  ) : null}
                  <span>{o.label}</span>
                </button>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
}
